import { useMemo } from 'react';

import {
	useCreationFocusedNavLand,
	loading,
	d0,
	d1,
} from '@confluence/experiment-cfind-4473-creation-focused-nav/entry-points/useCreationFocusedNavLand.ts';
import type { MenuId } from '../PlatformNavigationTypes.ts';

export type MenuVisibility = {
	menuId: MenuId;
	visible: boolean;
};

// Hidden by default; GSN only applies these when the user has no saved customizations.
const BASE_MENU_VISIBILITY: MenuVisibility[] = [
	{ menuId: 'drafts', visible: false },
	{ menuId: 'tasks', visible: false },
	{ menuId: 'templates', visible: false },
];

// Hidden for "simplify nav" treatments. "For You" is excluded: GSN renders it non-hideable.
const STANDARD_ITEMS_HIDDEN: MenuVisibility[] = [
	{ menuId: 'recent', visible: false },
	{ menuId: 'starred', visible: false },
	{ menuId: 'companyHubShortcut', visible: false },
	{ menuId: 'teamsShortcut', visible: false },
];

// Later entries win per menuId, so overrides take precedence over the base defaults.
const mergeMenuVisibility = (
	base: MenuVisibility[],
	overrides: MenuVisibility[],
): MenuVisibility[] => {
	const byMenuId = new Map<MenuId, MenuVisibility>();
	for (const item of [...base, ...overrides]) {
		byMenuId.set(item.menuId, item);
	}
	return Array.from(byMenuId.values());
};

/**
 * Platform-nav equivalent of `usePlatformCustomizationsWithSimplifyNav`, layering the cfind-4473
 * experiment cohorts on top of the base defaults. GSN owns the user-customization store and applies
 * user preferences over these defaults.
 */
export const useDefaultMenuVisibility = (): MenuVisibility[] => {
	const { navCustomization } = useCreationFocusedNavLand(true, false);

	return useMemo(() => {
		let experimentOverrides: MenuVisibility[] = [];

		if (navCustomization === loading) {
			experimentOverrides = [...STANDARD_ITEMS_HIDDEN, { menuId: 'templates', visible: false }];
		} else if (navCustomization === d0) {
			experimentOverrides = [...STANDARD_ITEMS_HIDDEN, { menuId: 'templates', visible: true }];
		} else if (navCustomization === d1) {
			experimentOverrides = [{ menuId: 'templates', visible: true }];
		}

		return mergeMenuVisibility(BASE_MENU_VISIBILITY, experimentOverrides);
	}, [navCustomization]);
};
